import { Box, Stack } from "@mui/material";
import {
  mapMerchantsFiltersToParams,
  mapMerchantsSortColumn,
  useMerchantsListQuery,
  type MerchantsListParams,
} from "Api/Queries/Merchants/useGetMerchantsQuery";
import { useMerchantStatsQuery } from "Api/Queries/Merchants/useMerchantStatsQuery";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { MerchantsStatusWidgets } from "Components/Hunter/Merchants/MerchantsList/MerchantsStatusWidgets";
import { MerchantsStatusWidgetsSkeleton } from "Components/Hunter/Merchants/MerchantsList/MerchantsStatusWidgetsSkeleton";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { MerchantListTable } from "Components/Merchants/MerchantListTable/MerchantListTable";
import { OrbitLOnboardingTable } from "Components/Merchants/OrbitLOnboarding/OrbitLOnboardingTable";
import { DownloadInvoicesSection } from "Components/Shared/DownloadInvoicesSection/DownloadInvoicesSection";
import { SettlementInvoiceType } from "Infrastructure/Api/Api";
import type { MerchantListItemDto } from "Infrastructure/Api/Api";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { Resources, useResource } from "Translations/Resources";
import { useIsCurrentUserAML } from "Hooks/useIsCurrentUserAml";
import { useIsCurrentUserAdmin } from "Hooks/useIsCurrentUserAdmin";
import { useIsCurrentUserCustomerOnboarding } from "Hooks/useIsCurrentUserCustomerOnboarding";
import { appUrl } from "Utils/UrlUtils";
import { useServerTable } from "Hooks/useServerTable";

export const MerchantListPage = () => {
  const isAml = useIsCurrentUserAML();
  const isAdmin = useIsCurrentUserAdmin();
  const isCustomerOnboarding = useIsCurrentUserCustomerOnboarding();
  const isAdminOrCO = isAdmin || isCustomerOnboarding;
  const { t } = useResource();

  const { queryParams, tableProps } = useServerTable<MerchantListItemDto>({
    defaultSorting: [{ id: "dateCreated", desc: true }],
  });

  const apiParams = useMemo(
    (): MerchantsListParams => ({
      offset: queryParams.offset,
      limit: queryParams.limit,
      sortBy: mapMerchantsSortColumn(queryParams.sortBy),
      sortDirection: queryParams.sortDirection,
      ...mapMerchantsFiltersToParams(queryParams.filters),
    }),
    [queryParams],
  );

  const { data, isLoading, isRefetching, isError } =
    useMerchantsListQuery(apiParams);
  const { data: statsData, isLoading: isLoadingStats } =
    useMerchantStatsQuery();

  if (isError) {
    return (
      <Layout>
        <ErrorText>{t(Resources.Pages.Content.Merchants.Error)}</ErrorText>
      </Layout>
    );
  }

  return (
    <Layout>
      {!isAml &&
        (isLoadingStats ? (
          <MerchantsStatusWidgetsSkeleton />
        ) : statsData ? (
          <MerchantsStatusWidgets
            openRegistrations={statsData.openRegistrations}
            invitationsSent={statsData.invitationsSent}
            inOnboarding={statsData.inOnboarding}
            completed={statsData.completed}
          />
        ) : null)}
      {isAdmin && (
        <DownloadInvoicesSection invoiceType={SettlementInvoiceType.Merchant} />
      )}
      <Box display="grid" alignItems="start">
        <MerchantListTable
          isLoading={isLoading}
          isRefetching={isRefetching}
          data={data?.items || []}
          rowCount={data?.totalCount ?? 0}
          tableProps={tableProps}
        />
      </Box>
      {isAdminOrCO && <OrbitLOnboardingTable isEditingEnabled />}
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout
      title={t(Resources.Pages.Titles.Merchants)}
      backNavigation={{
        mode: "historyStack",
        fallbackUrl: appUrl("dashboard"),
      }}
    >
      <StyledPageWrapper maxWidth="unset !important">
        <Stack gap={4}>{children}</Stack>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default MerchantListPage;
